import React, { useState, useContext, useEffect } from "react";
import { Container } from "reactstrap";
import { useParams } from "react-router-dom";
import { MASTER_AE_PATHS } from "../../../../Routes/paths";
import { useAdverseEventApi } from "../api/useAdverseEventApi";
import { useAdverseEventFollowupApi } from "../api/useAdverseEventFollowupApi";
import { useAdverseEventMedicationsApi } from "../api/useAdverseEventMedicationsApi";
import { useAdverseEventAssessmentApi } from "../api/useAdverseEventAssessmentApi";
import { mapMedicationsDataToForm } from "../helper/adverseeventconstant";
import { formatDate, formatTime24Hour } from "common/service/CommonService";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import CommonTableIndexWise, { Column } from "Components/Common/CommonTableIndexWise";

import { BreadcrumbContext } from "Layouts/Header";
import FullPageLoader from "Components/Common/FullPageLoader";
import SectionCard from "Components/Common/SectionCard";
import SkeletonLoader from "Components/Common/ViewSkeleton";
import DetailField from "Components/Common/DetailField";
import { Row } from "reactstrap";

import AdverseEventDetailViewTab from "../components/AdverseEventDetailViewTab";
import AdverseEventFollowupViewTab from "../components/AdverseEventFollowupViewTab";

// ─── Tab Key Type ────────────────────────────────────────────────────────────
type AEViewTab = "event_details" | "followups" | "medications" | "pi_assessment";

const TABS: { key: AEViewTab; label: string; icon: string }[] = [
  { key: "event_details", label: "Event Details", icon: "ri-pulse-line" },
  { key: "followups", label: "Follow-ups", icon: "ri-file-list-3-line" },
  { key: "medications", label: "Medications", icon: "ri-capsule-line" },
  { key: "pi_assessment", label: "PI Assessment", icon: "ri-shield-user-line" },
];

// ─── Component ───────────────────────────────────────────────────────────────
const AdverseEventView: React.FC = () => {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState<AEViewTab>("event_details");

  const { SelectByIdQuery } = useAdverseEventApi({
    adverseeventid: Number(id),
  });

  const { SelectQuery: followupQuery } = useAdverseEventFollowupApi({
    adverseeventid: Number(id),
    enabled: activeTab === "followups",
  });

  const { SelectQuery: medicationsQuery } = useAdverseEventMedicationsApi({
    adverseeventid: Number(id),
    enabled: activeTab === "medications",
  });

  const { SelectQuery: assessmentQuery } = useAdverseEventAssessmentApi({
    adverseeventid: Number(id),
    enabled: activeTab === "pi_assessment",
  });

  const followups: any[] = followupQuery.data?.data || [];
  const medicationsDataRaw: any = medicationsQuery.data?.data;
  const mappedMeds = medicationsDataRaw ? mapMedicationsDataToForm(medicationsDataRaw, (d: string) => d) : null;
  const medicines: any[] = (() => {
    if (medicationsDataRaw) {
      if (Array.isArray(medicationsDataRaw)) return medicationsDataRaw;
      if (Array.isArray(medicationsDataRaw.medicines)) return medicationsDataRaw.medicines;
    }
    return SelectByIdQuery.data?.data?.[0]?.medicines || [];
  })();

  const assessmentRec: any = (() => {
    const a = assessmentQuery.data?.data;
    if (!a) return null;
    return Array.isArray(a) ? a[0] : a;
  })();

  

  const breadcrumbContext = useContext(BreadcrumbContext);
  const setBreadcrumbItems = breadcrumbContext?.setBreadcrumbItems;

  useEffect(() => {
    setBreadcrumbItems?.([
      { text: "Adverse Event", link: MASTER_AE_PATHS.LIST },
      { text: "View Adverse Event" },
    ]);

    return () => {
      setBreadcrumbItems?.([]);
    };
  }, []);

  const data = SelectByIdQuery.data?.data?.[0];

  const consumedMedicationText = (() => {
    const v = mappedMeds?.consumedmedication ?? data?.medicineconsumed ?? data?.consumedmedication;
    if (v === null || v === undefined || v === "") return "—";
    if (typeof v === "boolean") return v ? "Yes" : "No";
    const s = String(v).trim();
    const lower = s.toLowerCase();
    if (["y", "yes", "true"].includes(lower)) return "Yes";
    if (["n", "no", "false"].includes(lower)) return "No";
    return s.charAt(0).toUpperCase() + s.slice(1);
  })();

  if (SelectByIdQuery.isLoading || SelectByIdQuery.isFetching) {
    return (
      <Container fluid className={`px-0 ${formStyles.pageContainer}`}>
        <FullPageLoader message="Fetching details..." />
      </Container>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>

        {/* ── Page Header ─────────────────────────────────────────────── */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h3 className="fw-700 mb-1">Adverse Event Record</h3>
            {data && (
              <p className="text-muted mb-0">
                Adverse Event No: {data.adverseeventnumber || id} | Study Code:{" "}
                {data.studycode} | Subject No: {data.subjectno}
              </p>
            )}
          </div>
        </div>

        {/* ── Tab Switcher ─────────────────────────────────────────────── */}
        <div className="mb-4">
          <div className="p-1 bg-light rounded-pill d-inline-flex border">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`btn btn-sm px-3 rounded-pill fw-semibold border-0 d-flex align-items-center gap-1 ${
                  activeTab === tab.key ? "text-primary shadow-sm" : "text-muted"
                }`}
                style={{
                  fontSize: "12px",
                  paddingTop: "4px",
                  paddingBottom: "4px",
                  backgroundColor:
                    activeTab === tab.key ? "#ffffff" : "transparent",
                  transition: "background-color 0.15s ease",
                }}
                onClick={() => setActiveTab(tab.key)}
              >
                <i className={tab.icon} style={{ fontSize: "13px" }} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Tab Content ──────────────────────────────────────────────── */}
        <div className="d-flex flex-column gap-3">

          {/* ERROR / NOT FOUND STATE */}
          {!data && (
            <div className="text-center py-5">
              <i className="ri-error-warning-line display-3 text-warning mb-3 d-block" />
              <h4 className="fw-600">Event Not Found</h4>
              <p className="text-muted">
                The adverse event record you are looking for does not exist or has
                been removed.
              </p>
            </div>
          )}

          {/* TAB 1: Event Details */}
          {data && activeTab === "event_details" && (
            <AdverseEventDetailViewTab data={data} id={id} />
          )}

          {/* TAB 2: Follow-ups */}
          {data && activeTab === "followups" && (
            <AdverseEventFollowupViewTab
              isLoading={followupQuery.isLoading}
              isFetching={followupQuery.isFetching}
              followups={followups}
            />
          )}

          {/* TAB 3: Medications */}
          {data && activeTab === "medications" && (
            <>
              <div className={formStyles.sectionCard}>
                <div className={formStyles.sectionHeader}>
                  <i className={`ri-capsule-line ${formStyles.sectionIcon}`} />
                  <div className={formStyles.sectionTitleWrap}>
                    <p className={formStyles.sectionTitle}>Medicines used to treat adverse event</p>
                    <p className={formStyles.sectionSubtitle}>List of medications administered during the event.</p>
                  </div>
                </div>
              </div>

              { (medicationsQuery?.isLoading || medicationsQuery?.isFetching) ? (
                <SkeletonLoader sections={1} fieldsPerSection={6} showHeader={false} />
              ) : (
                <>
                  {(!medicines || medicines.length === 0) ? (
                    <div
                      className="d-flex flex-column align-items-center justify-content-center py-4 text-center"
                      style={{ minHeight: "150px" }}
                    >
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center mb-2"
                        style={{
                          width: 48,
                          height: 48,
                          background: "rgba(6,182,212,0.08)",
                        }}
                      >
                        <i className="ri-capsule-line" style={{ fontSize: 24, color: "#06b6d4" }} />
                      </div>
                      <h6 className="fw-semibold text-dark mb-1">No medicines recorded yet</h6>
                    </div>
                  ) : (
                    medicines.map((med: any, idx: number) => (
                      <SectionCard key={med.medicineid || idx} icon="ri-capsule-line" title={`Medicine ${String(idx + 1).padStart(2, "0")}`}>
                        <Row>
                          <DetailField label="Brand Name" value={med.brandname || null} />
                          <DetailField label="Generic Name" value={med.genericname || null} />
                          <DetailField label="Frequency" value={med.frequency || null} />
                          <DetailField label="Route" value={med.routename || med.route || null} />
                          <DetailField label="Start Date" value={med.startdate ? formatDate(med.startdate) : null} />
                          <DetailField label="End Date" value={med.enddate ? formatDate(med.enddate) : null} />
                          <DetailField label="Interaction with I.P." value={med.interactionwithip || med.interaction || null} />
                        </Row>
                      </SectionCard>
                    ))
                  )}
                </>
              )}

              <SectionCard
                icon="ri-time-line"
                title="Resolution Details"
                subtitle="Capture resolution timing, date, recorder details and medication consumption."
              >
                <div className="row g-3">
                  <div className="col-md-4">
                    <span className="text-muted fs-12 d-block">Resolution Date</span>
                    <span className="text-dark fs-14">{mappedMeds?.resolutiondate ? formatDate(mappedMeds.resolutiondate) : (data?.resolutiondate ? formatDate(data.resolutiondate) : "—")}</span>
                  </div>
                  <div className="col-md-4">
                    <span className="text-muted fs-12 d-block">Resolution Time</span>
                    <span className="text-dark fs-14">{mappedMeds?.resolutiontime ? formatTime24Hour(mappedMeds.resolutiontime) : (data?.resolutiontime ? formatTime24Hour(data.resolutiontime) : "—")}</span>
                  </div>
                  <div className="col-md-4">
                    <span className="text-muted fs-12 d-block">Has participant consumed all prescribed medication?</span>
                    <span className="text-dark fs-14">{consumedMedicationText}</span>
                  </div>
                </div>
              </SectionCard>
            </>
          )}

          {/* TAB 4: PI Assessment */}
          {data && activeTab === "pi_assessment" && (
            (assessmentQuery.isLoading || assessmentQuery.isFetching) ? (
              <SkeletonLoader sections={1} fieldsPerSection={7} showHeader={false} />
            ) : (
              <SectionCard
                icon="ri-shield-user-line"
                title="Principal Investigator / Designee Assessment"
                subtitle="Severity, Causality, Action Taken, Outcome and Relationship assessment."
              >
                <div className="row g-3">
                  <div className="col-md-6">
                    <span className="text-muted fs-12 d-block">Final Diagnosis</span>
                    <span className="text-dark fs-14">{(assessmentRec && (assessmentRec.finaldiagnosis || assessmentRec.finaldiagnosis === "")) ? (assessmentRec.finaldiagnosis || "—") : (data.finaldiagnosis || "—")}</span>
                  </div>
                  <div className="col-md-6">
                    <span className="text-muted fs-12 d-block">Severity</span>
                    <span className="text-dark fs-14">{assessmentRec?.piseverityname || data.piseverityname || assessmentRec?.severity || data.piseverity || "—"}</span>
                  </div>
                  <div className="col-md-6">
                    <span className="text-muted fs-12 d-block">Causality</span>
                    <span className="text-dark fs-14">{assessmentRec?.picausalityname || data.picausalityname || assessmentRec?.causality || data.picausality || "—"}</span>
                  </div>
                  <div className="col-md-6">
                    <span className="text-muted fs-12 d-block">Action Taken</span>
                    <span className="text-dark fs-14">{assessmentRec?.piactiontakenname || data.piactiontakenname || assessmentRec?.actiontaken || data.piactiontaken || "—"}</span>
                  </div>
                  <div className="col-md-6">
                    <span className="text-muted fs-12 d-block">Outcome of Event</span>
                    <span className="text-dark fs-14">{assessmentRec?.outcomeofeventname || data.outcomeofeventname || assessmentRec?.outcomeofevent || data.outcomeofevent || "—"}</span>
                  </div>
                  <div className="col-md-6">
                    <span className="text-muted fs-12 d-block">Likelihood</span>
                    <span className="text-dark fs-14">{assessmentRec?.likelihoodname || data.likelihoodname || assessmentRec?.likelihood || data.likelihood || "—"}</span>
                  </div>
                  <div className="col-md-6">
                    <span className="text-muted fs-12 d-block">Relationship</span>
                    <span className="text-dark fs-14">{assessmentRec?.relationshipname || data.relationshipname || assessmentRec?.relationship || data.relationship || "—"}</span>
                  </div>
                </div>
              </SectionCard>
            )
          )}

        </div>
      </Container>
    </div>
  );
};

export default AdverseEventView;
